import { useEffect, useRef } from 'react';
import { useCamera } from '@/contexts/CameraContext';
import { Camera, CameraOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function CameraPreview() {
  const { isEnabled, isStreaming, stream, enable, disable } = useCamera();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Update video element when stream changes
  useEffect(() => {
    if (videoRef.current) {
      if (stream) {
        console.log('📷 CameraPreview: Setting stream to video element', stream.id);
        videoRef.current.srcObject = stream;
        // Force video to play
        videoRef.current.play().catch(err => {
          console.error('📷 CameraPreview: Failed to play video:', err);
        });
      } else {
        console.log('📷 CameraPreview: Clearing video element stream');
        videoRef.current.srcObject = null;
      }
    }
  }, [stream]);

  const handleToggle = async () => {
    if (isStreaming) {
      disable();
    } else {
      try {
        await enable();
      } catch (error) {
        console.error('Failed to enable camera:', error);
      }
    }
  };

  if (!isEnabled) {
    return (
      <div className="p-4 bg-muted/30 rounded-lg border border-dashed">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-foreground">Camera</h4>
          <CameraOff className="w-4 h-4 text-muted-foreground" />
        </div>
        <p className="text-xs text-muted-foreground">
          Camera disabled in settings
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-muted/30 rounded-lg border">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-foreground">Camera Preview</h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleToggle}
          className="h-8 px-2"
        >
          {isStreaming ? (
            <CameraOff className="w-4 h-4" />
          ) : (
            <Camera className="w-4 h-4" />
          )}
        </Button>
      </div>

      {isStreaming ? (
        <div className="relative aspect-video bg-black rounded-md overflow-hidden">
          <video
            key={stream?.id || 'no-stream'}
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          <div className="absolute top-2 left-2 px-2 py-1 bg-red-500/90 text-white text-xs rounded-full flex items-center gap-1">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            LIVE
          </div>
        </div>
      ) : (
        <div className="aspect-video bg-muted rounded-md flex items-center justify-center">
          <div className="text-center">
            <Camera className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Camera off</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggle}
              className="mt-2"
            >
              Enable Camera
            </Button>
          </div>
        </div>
      )}

    </div>
  );
}
